/**
 * Read-only "what you'll get" preview of a blog template's generation config.
 * Reads straight from the template's default_config (already on BlogTemplateSummary),
 * so no fetch is needed. Used inside TemplatePreviewModal across every creation surface.
 */
import React from 'react';
import { FileText, ListTree, Image as ImageIcon, Link2 } from 'lucide-react';
import type { TemplateDefaultConfig } from '@/services/blogTemplatesService';

interface AutoTemplateConfigPreviewProps {
  config?: TemplateDefaultConfig | null;
}

const AI = 'AI decides';
const DASH = '—';

const cap = (s?: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : DASH);

function range(min?: number, max?: number, fallback = AI): string {
  if (min == null && max == null) return fallback;
  if (min != null && max != null) return min === max ? `${min}` : `${min}–${max}`;
  return `${min ?? max}`;
}

interface Group {
  icon: React.ReactNode;
  title: string;
  rows: [string, string][];
}

export const AutoTemplateConfigPreview: React.FC<AutoTemplateConfigPreviewProps> = ({ config }) => {
  const c = config || {};
  const a = c.autoConfig || {};

  const groups: Group[] = [
    {
      icon: <FileText className="h-4 w-4 text-teal-dark" />,
      title: 'Content',
      rows: [
        ['Target length', c.targetWordCount ? `${c.targetWordCount.toLocaleString()} words` : AI],
        ['Tone', cap(c.tone)],
        ['FAQ section', c.includesFAQ || a.includeFAQ ? 'Included' : DASH],
        ['Schema markup', c.includesSchema ? 'Included' : DASH],
      ],
    },
    {
      icon: <ListTree className="h-4 w-4 text-teal-dark" />,
      title: 'Structure',
      rows: [
        ['Sections', range(a.minSections, a.maxSections)],
        ['List', a.includeList ? `${a.listType ? cap(a.listType) : 'Yes'}${a.maxListItems ? ` (up to ${a.maxListItems})` : ''}` : DASH],
      ],
    },
    {
      icon: <ImageIcon className="h-4 w-4 text-teal-dark" />,
      title: 'Images',
      rows: a.includeImages
        ? [
            ['Count', range(a.minImages, a.maxImages)],
            ['Style', cap(a.imageStyle)],
            ['Placement', cap(a.imagePosition)],
          ]
        : [['Images', DASH]],
    },
    {
      icon: <Link2 className="h-4 w-4 text-teal-dark" />,
      title: 'Linking',
      rows: [
        ['Internal links', String(c.internalLinkCount ?? a.internalLinkCount ?? AI)],
        ['External links', String(c.externalLinkCount ?? a.externalLinkCount ?? AI)],
        ['Strategy', cap(c.linkStrategy || a.linkStrategy)],
      ],
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {groups.map((g) => (
        <div key={g.title} className="rounded-lg border border-border bg-muted/30 p-3">
          <div className="mb-2 flex items-center gap-2">
            {g.icon}
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {g.title}
            </span>
          </div>
          <dl className="space-y-1">
            {g.rows.map(([k, v]) => (
              <div key={k} className="flex items-baseline justify-between gap-3 text-sm">
                <dt className="text-muted-foreground">{k}</dt>
                <dd className="text-right font-medium tabular-nums">{v}</dd>
              </div>
            ))}
          </dl>
        </div>
      ))}
    </div>
  );
};
